import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
// import ResponsiveTable from "@saleor/components/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import classNames from "classnames";
import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";

import { maybe, renderCollection } from "../../../misc";
import { VoucherDetails_voucher_malls } from "../../types/VoucherDetails";

export interface MallListProps {
  malls: VoucherDetails_voucher_malls;
  disabled: boolean;
  emptyText: React.ReactNode;
  title: React.ReactNode;
  onMallAssign: () => void;
  onMallUnassign: (id: string) => void;
}

const useStyles = makeStyles(
  theme => ({
    buttonDisabled: {
      "& > span": {
        color: "#616161"
      }
    },
    iconCell: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 48 + theme.spacing(2)
    },
    indicator: {
      color: theme.palette.text.disabled,
      display: "inline-block",
      left: 0,
      marginRight: theme.spacing(0.5),
      position: "absolute"
    },
    offsetCell: {
      "&:first-child": {
        paddingLeft: theme.spacing(3)
      },
      position: "relative",
      width: "90%"
    },
    pointer: {
      cursor: "pointer"
    },
    root: {
      "&:last-child": {
        paddingBottom: 0
      },
      paddingTop: 0
    },
    rotate: {
      transform: "rotate(180deg)"
    },
    textRight: {
      textAlign: "right"
    },
    toLeft: {
      "&:first-child": {
        paddingLeft: 0
      },
      width: "100%"
    },
    wideColumn: {
      width: "100%"
    }
  }),
  { name: "VoucherMallList" }
);

const VoucherMallList: React.FC<MallListProps> = props => {
  const {
    malls,
    disabled,
    emptyText,
    title,
    onMallAssign,
    onMallUnassign
  } = props;
  const classes = useStyles(props);

  const [isCollapsed, setCollapseStatus] = React.useState(true);
  const toggleCollapse = () => setCollapseStatus(!isCollapsed);

  const mallList = maybe(() => malls.edges.map(edges => edges.node));
  const totalCount = maybe(() => malls.totalCount, 0);

  useEffect(() => {
    if (totalCount === 0) {
      setCollapseStatus(true);
    }
  }, [totalCount]);

  return (
    <Card>
      <CardTitle
        title={title}
        toolbar={
          <Button
            id="assignMallsDialogBtn"
            color="primary"
            disabled={disabled}
            className={disabled ? classes.buttonDisabled : null}
            onClick={onMallAssign}
          >
            <FormattedMessage
              defaultMessage="Assign Malls"
              description="button"
            />
          </Button>
        }
      />
      <CardContent className={classes.root}>
        {/* <ResponsiveTable> */}
        <TableBody>
          <TableRow
            className={classes.pointer}
            onClick={totalCount > 0 && toggleCollapse}
          >
            <TableCell
              className={classNames(classes.wideColumn, classes.toLeft)}
            >
              {totalCount === 0 ? (
                <FormattedMessage
                  defaultMessage="Applied to all malls"
                  description="number of malls"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{number} Mall{plural}"
                  description="number of malls"
                  values={{
                    number: maybe(() => malls.totalCount.toString()),
                    plural: maybe(() => (malls.totalCount > 1 ? "s" : ""))
                  }}
                />
              )}
            </TableCell>
            <TableCell
              className={classNames(classes.textRight, classes.iconCell)}
            >
              {totalCount > 0 && (
                <IconButton id="mallDropdownBtn">
                  <ArrowDropDownIcon
                    className={classNames({
                      [classes.rotate]: !isCollapsed
                    })}
                  />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
          {!isCollapsed &&
            renderCollection(
              mallList,
              (mall, index) => (
                <TableRow key={mall ? mall.id : "skeleton"}>
                  <TableCell
                    className={classes.offsetCell}
                    id={`mallNameRow${index}`}
                  >
                    {maybe<React.ReactNode>(
                      () => (
                        <>{mall.name}</>
                      ),
                      <Skeleton />
                    )}
                  </TableCell>
                  <TableCell
                    className={classNames(classes.textRight, classes.iconCell)}
                    id={`mallUnassignBtnRow${index}`}
                  >
                    <IconButton
                      color="primary"
                      disabled={!mall || disabled}
                      onClick={() => onMallUnassign(mall.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ),
              () => (
                <TableRow>
                  <TableCell className={classes.toLeft} colSpan={2}>
                    {emptyText}
                  </TableCell>
                </TableRow>
              )
            )}
        </TableBody>
        {/* </ResponsiveTable> */}
      </CardContent>
    </Card>
  );
};
export default VoucherMallList;
